import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "wouter";
import {
  AlertTriangle, AudioLines, Award, BarChart3, Bot, Brain, Briefcase, Calendar,
  CalendarCheck, Clock, Columns3, Compass, Cpu, Eye, FileSearch, FileText, Filter,
  Gauge, GitBranch, Globe, GraduationCap, HandCoins, Heart, KanbanSquare, Layers,
  LineChart, Link2, ListChecks, ListOrdered, MessageSquareText, Mic, Network,
  PenLine, PieChart, Plug, Quote, RefreshCw, Scale, ScanSearch, Send, Settings, Share2,
  ShieldCheck, Shuffle, Sliders, Target, TrendingUp, Video, Wrench, Zap,
  ArrowRight, Check, HelpCircle, type LucideIcon,
} from "lucide-react";
import { WebsiteLayout } from "@/components/layouts/WebsiteLayout";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSEO } from "@/hooks/useSEO";
import { featureStructuredData } from "@shared/structuredData";
import {
  FEATURE_CONTENT_BY_SLUG,
  WORKS_WITH_ATS_LINE,
  type FeatureCta,
  type FeatureContent,
  type FeatureVisual,
} from "@shared/featureContent";
import { BrowserFrame } from "@/components/website/demo/BrowserFrame";
import { DEMO_RESERVED_HEIGHT, type DemoCrop } from "@/components/website/product-demo/DemoScreen";
import {
  DEMO_SCREENS,
  demoScreenHint,
  type DemoScreenComponent,
  type DemoScreenName,
} from "@/components/website/product-demo/lazyScreens";
import NotFound from "@/pages/not-found";

const BASE_URL = "https://www.talenture.ai";

const ICONS: Record<string, LucideIcon> = {
  AlertTriangle, AudioLines, Award, BarChart3, Bot, Brain, Briefcase, Calendar,
  CalendarCheck, Clock, Columns3, Compass, Cpu, Eye, FileSearch, FileText, Filter,
  Gauge, GitBranch, Globe, GraduationCap, HandCoins, Heart, KanbanSquare, Layers,
  LineChart, Link2, ListChecks, ListOrdered, MessageSquareText, Mic, Network,
  PenLine, PieChart, Quote, RefreshCw, Scale, ScanSearch, Send, Settings, Share2,
  ShieldCheck, Shuffle, Sliders, Target, TrendingUp, Video, Zap,
};

function iconFor(name: string): LucideIcon {
  return ICONS[name] ?? Layers;
}

/**
 * The real product screens, mounted from the same components the app renders
 * and fed fixture data. A feature page names one of these in
 * `visual.component`; anything unrecognised renders nothing rather than
 * breaking the page.
 */
const VISUALS: Record<string, DemoScreenComponent> = DEMO_SCREENS;

/** External CTAs (the founder's booking page) must be real anchors, not wouter links. */
function isExternal(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}

/**
 * Anchor CTAs (`/#sample-scorecard`) cannot be wouter <Link>s either: on the
 * page they already point at, wouter will not re-navigate and the browser never
 * scrolls. A plain <a> always resolves the fragment.
 */
function CtaButton({
  cta,
  variant,
  children,
  testId,
}: {
  cta: FeatureCta;
  variant?: "outline";
  children: ReactNode;
  testId: string;
}) {
  const external = isExternal(cta.href);
  return (
    <Button size="lg" variant={variant} className="w-full sm:w-auto" asChild data-testid={testId}>
      <a
        href={cta.href}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    </Button>
  );
}

function CtaButtons({ feature }: { feature: FeatureContent }) {
  const { primaryCta, secondaryCta } = feature.hero;
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="feature-cta-group">
      <CtaButton cta={primaryCta} testId="button-feature-primary-cta">
        {primaryCta.label}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </CtaButton>
      {secondaryCta && (
        <CtaButton cta={secondaryCta} variant="outline" testId="button-feature-secondary-cta">
          {secondaryCta.label}
        </CtaButton>
      )}
    </div>
  );
}

function HeroSection({ feature }: { feature: FeatureContent }) {
  const HeroIcon = iconFor(feature.icon);
  return (
    <section className="bg-gradient-to-b from-indigo-50 to-white dark:from-gray-900 dark:to-gray-950 border-b border-gray-100 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-100 dark:bg-indigo-950 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-6">
          <HeroIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {feature.hero.eyebrow}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 dark:text-white max-w-3xl" data-testid="text-feature-h1">
          {feature.hero.h1}
        </h1>
        {feature.hero.painOpener && (
          <p
            className="mt-6 max-w-2xl text-lg italic text-gray-500 dark:text-gray-400"
            data-testid="text-feature-pain-opener"
          >
            {feature.hero.painOpener}
          </p>
        )}
        <p
          className={`${feature.hero.painOpener ? "mt-3" : "mt-6"} text-lg text-gray-600 dark:text-gray-300 max-w-2xl`}
        >
          {feature.hero.subhead}
        </p>
        {feature.hero.worksWithAts && (
          <p
            className="mt-5 flex items-start gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            data-testid="text-feature-works-with-ats"
          >
            <Plug className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
            {WORKS_WITH_ATS_LINE}
          </p>
        )}
        {feature.hero.whereThisFits && (
          <p
            className="mt-6 max-w-2xl border-l-2 border-indigo-300 dark:border-indigo-700 pl-4 text-sm text-indigo-900 dark:text-indigo-200"
            data-testid="text-feature-where-this-fits"
          >
            {feature.hero.whereThisFits}
          </p>
        )}
        <div className="mt-8">
          <CtaButtons feature={feature} />
        </div>
      </div>
    </section>
  );
}

function ProofStrip({ feature }: { feature: FeatureContent }) {
  if (feature.proofPoints.length === 0) return null;
  return (
    <section className="bg-gray-900 dark:bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {feature.proofPoints.map((point) => (
            <div key={point.label} data-testid="feature-proof-point">
              <dt className="text-3xl font-bold text-indigo-300">{point.value}</dt>
              <dd className="mt-1 text-sm text-gray-300">{point.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function DefinitionSection({ feature }: { feature: FeatureContent }) {
  return (
    <section className="bg-white dark:bg-gray-950">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {feature.definition.heading}
        </h2>
        <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
          {feature.definition.body}
        </p>
      </div>
    </section>
  );
}

function ScorecardConnector({ feature }: { feature: FeatureContent }) {
  if (!feature.scorecardConnector) return null;
  return (
    <section className="bg-indigo-50 dark:bg-indigo-950/40 border-b border-indigo-100 dark:border-indigo-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Layers className="h-5 w-5 shrink-0 text-indigo-600 dark:text-indigo-300 mt-0.5" aria-hidden="true" />
          <p className="text-sm sm:text-base text-indigo-900 dark:text-indigo-100">{feature.scorecardConnector}</p>
        </div>
        <Link
          href="/what-you-get"
          className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:underline whitespace-nowrap"
          data-testid="link-feature-scorecard"
        >
          See the full scorecard
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

/**
 * Where each supporting visual lands: spread evenly through the value sections
 * so a screen sits next to the claim it backs, and the last one still has a
 * section under it rather than trailing off the block.
 */
function visualPlacement(
  sectionCount: number,
  visuals: FeatureVisual[],
): Map<number, FeatureVisual> {
  const placement = new Map<number, FeatureVisual>();
  if (sectionCount === 0) return placement;
  visuals.forEach((visual, index) => {
    const after = Math.min(
      sectionCount - 1,
      Math.round(((index + 1) * sectionCount) / (visuals.length + 1)) - 1,
    );
    // Two visuals colliding on the same slot would stack; push the later one down.
    let slot = Math.max(0, after);
    while (placement.has(slot) && slot < sectionCount - 1) slot += 1;
    placement.set(slot, visual);
  });
  return placement;
}

function ValueSections({ feature }: { feature: FeatureContent }) {
  const placement = visualPlacement(
    feature.sections.length,
    feature.supportingVisuals ?? [],
  );
  return (
    <section className="bg-gray-50 dark:bg-gray-900 border-y border-gray-100 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-12">
        {feature.sections.map((section, index) => {
          const SectionIcon = iconFor(section.icon);
          const visual = placement.get(index);
          return (
            <div key={section.title}>
            <div
              className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-8 items-start"
              data-testid="feature-value-section"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300">
                <SectionIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {section.title}
                </h3>
                <p className="mt-2 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {section.body}
                </p>
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                        <Check className="h-5 w-5 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {visual && (
              <div className="mt-12">
                <VisualFrame visual={visual} crop="preview" lazy />
              </div>
            )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Mounts its children the first time they come near the viewport.
 *
 * A product screen is a real slice of the app — the candidate panel alone
 * mounts nine destinations — so a page carrying three of them would pay for all
 * three before a visitor has scrolled past the hero. The placeholder reserves
 * height so the page does not jump when the screen arrives.
 */
function LazyMount({ children, reserve }: { children: ReactNode; reserve: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const node = ref.current;
    // No IntersectionObserver (older browsers, jsdom): show it rather than
    // leave a permanent blank.
    if (!node || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(node);
    // Printing does not scroll, so nothing below the fold would ever
    // intersect: a printed page would carry blank boxes where the screens are.
    const showEverything = () => setShown(true);
    window.addEventListener("beforeprint", showEverything);
    const printQuery = window.matchMedia?.("print");
    printQuery?.addEventListener?.("change", showEverything);
    return () => {
      observer.disconnect();
      window.removeEventListener("beforeprint", showEverything);
      printQuery?.removeEventListener?.("change", showEverything);
    };
  }, [shown]);

  return (
    <div ref={ref} className={shown ? undefined : reserve}>
      {shown ? children : null}
    </div>
  );
}

function VisualFrame({
  visual,
  crop,
  lazy = false,
}: {
  visual: FeatureVisual;
  crop: DemoCrop;
  lazy?: boolean;
}) {
  const Demo = VISUALS[visual.component];
  if (!Demo) return null;
  const frame = (
    <BrowserFrame url={visual.url} hint={demoScreenHint(visual.component as DemoScreenName)}>
      {/* Each demo is a separate chunk; hold the height while it arrives. */}
      <Suspense fallback={<div className={DEMO_RESERVED_HEIGHT[crop]} />}>
        <Demo crop={crop} />
      </Suspense>
    </BrowserFrame>
  );
  return (
    <div data-testid="feature-visual">
      {visual.heading && (
        <h3 className="mb-4 text-center text-xl font-semibold text-gray-900 dark:text-white">
          {visual.heading}
        </h3>
      )}
      {lazy ? <LazyMount reserve={DEMO_RESERVED_HEIGHT[crop]}>{frame}</LazyMount> : frame}
      <p
        className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400"
        data-testid="text-feature-visual-caption"
      >
        {visual.caption}
      </p>
    </div>
  );
}

/**
 * The lead screen, high on the page and uncropped on a desktop: a visitor
 * should be able to judge the feature here, without scrolling to the end.
 */
function ProductVisual({ feature }: { feature: FeatureContent }) {
  const visual = feature.visual;
  if (!visual) return null;
  return (
    <section className="bg-white dark:bg-gray-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <VisualFrame visual={visual} crop="lead" />
      </div>
    </section>
  );
}

function UnderTheHoodSection({ feature }: { feature: FeatureContent }) {
  const underTheHood = feature.underTheHood;
  if (!underTheHood) return null;
  return (
    <section className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="under-the-hood" className="border-0">
            <AccordionTrigger
              className="text-left text-lg font-semibold text-gray-900 dark:text-white"
              data-testid="trigger-feature-under-the-hood"
            >
              <span className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                Under the hood
              </span>
            </AccordionTrigger>
            <AccordionContent data-testid="content-feature-under-the-hood">
              <p className="text-gray-600 dark:text-gray-300">{underTheHood.intro}</p>
              <div className="mt-8 space-y-8">
                {underTheHood.sections.map((section) => {
                  const SectionIcon = iconFor(section.icon);
                  return (
                    <div key={section.title} data-testid="feature-under-the-hood-section">
                      <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                        <SectionIcon
                          className="h-4 w-4 text-indigo-600 dark:text-indigo-400"
                          aria-hidden="true"
                        />
                        {section.title}
                      </h3>
                      <p className="mt-2 text-gray-600 dark:text-gray-300 leading-relaxed">
                        {section.body}
                      </p>
                      {section.bullets && section.bullets.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {section.bullets.map((bullet) => (
                            <li
                              key={bullet}
                              className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                            >
                              <Check
                                className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400"
                                aria-hidden="true"
                              />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
}

function HowToSection({ feature }: { feature: FeatureContent }) {
  if (!feature.howTo) return null;
  return (
    <section className="bg-white dark:bg-gray-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {feature.howTo.heading}
        </h2>
        <p className="mt-3 text-gray-600 dark:text-gray-300">{feature.howTo.intro}</p>
        <ol className="mt-8 space-y-6">
          {feature.howTo.steps.map((step, index) => (
            <li key={step.title} className="flex gap-4" data-testid="feature-howto-step">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white font-semibold">
                {index + 1}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                <p className="mt-1 text-gray-600 dark:text-gray-300">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FaqSection({ feature }: { feature: FeatureContent }) {
  if (feature.faq.length === 0) return null;
  return (
    <section className="bg-gray-50 dark:bg-gray-900 border-y border-gray-100 dark:border-gray-800">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
          Frequently asked questions
        </h2>
        <Accordion type="single" collapsible className="mt-8 w-full">
          {feature.faq.map((entry, index) => (
            <AccordionItem
              key={entry.question}
              value={`faq-${index}`}
              data-testid="feature-faq-item"
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 dark:text-white">
                {entry.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {entry.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function RelatedSection({ feature }: { feature: FeatureContent }) {
  const related = feature.relatedSlugs
    .map((slug) => FEATURE_CONTENT_BY_SLUG[slug])
    .filter((item): item is FeatureContent => Boolean(item));
  if (related.length === 0) return null;
  return (
    <section className="bg-white dark:bg-gray-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Related features</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {related.map((item) => {
            const RelatedIcon = iconFor(item.icon);
            return (
              <Link
                key={item.slug}
                href={item.path}
                className="group rounded-xl border border-gray-200 dark:border-gray-800 p-6 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
                data-testid={`link-related-${item.slug}`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300">
                  <RelatedIcon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                  {item.name}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{item.navDescription}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ feature }: { feature: FeatureContent }) {
  return (
    <section className="bg-indigo-600 dark:bg-indigo-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-3xl font-bold text-white">{feature.hero.h1}</h2>
        <p className="mt-4 text-lg text-indigo-100 max-w-2xl mx-auto">{feature.hero.subhead}</p>
        <div className="mt-8 flex justify-center">
          <CtaButtons feature={feature} />
        </div>
      </div>
    </section>
  );
}

export default function FeaturePage() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug ?? "";
  const feature = FEATURE_CONTENT_BY_SLUG[slug];

  useSEO({
    title: feature?.seo.title ?? "Features | Talenture.ai",
    description: feature?.seo.description ?? "",
    keywords: feature?.seo.keywords ?? [],
    ogTitle: feature?.seo.ogTitle,
    ogDescription: feature?.seo.ogDescription,
    ogUrl: feature ? `${BASE_URL}${feature.path}` : undefined,
    ogImage: `${BASE_URL}/og-image.png`,
    canonical: feature ? `${BASE_URL}${feature.path}` : undefined,
    structuredData: feature ? featureStructuredData(feature) : undefined,
  });

  if (!feature) {
    return <NotFound />;
  }

  return (
    <WebsiteLayout variant={feature.audience}>
      <HeroSection feature={feature} />
      <ProofStrip feature={feature} />
      <DefinitionSection feature={feature} />
      <ScorecardConnector feature={feature} />
      <ProductVisual feature={feature} />
      <ValueSections feature={feature} />
      <HowToSection feature={feature} />
      <FaqSection feature={feature} />
      <UnderTheHoodSection feature={feature} />
      <RelatedSection feature={feature} />
      <ClosingCta feature={feature} />
    </WebsiteLayout>
  );
}
